import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Shield, Save, Loader2, SlidersHorizontal, KeyRound, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];

const TOGGLE_MODULES = [
  { key: "gout", label: "GOÛT" }, { key: "tagma", label: "TAGMA" },
  { key: "aura", label: "AURA" }, { key: "vetta", label: "VETTA" },
  { key: "crm", label: "CRM" }, { key: "contabilidad", label: "Finanzas" },
  { key: "shop-orders", label: "Shop Orders" }, { key: "automation", label: "Automatización" },
  { key: "master-metrics", label: "Métricas Maestras" }, { key: "intelligence", label: "Intelligence" },
];

const TIMEZONES = [
  ["", "Automática (según módulos conectados)"],
  ["America/Mexico_City", "Ciudad de México (GMT-6)"],
  ["America/Argentina/Buenos_Aires", "Buenos Aires, Argentina (GMT-3)"],
  ["America/Bogota", "Bogotá / Lima / Quito (GMT-5)"],
  ["America/Santiago", "Santiago de Chile"],
  ["America/Sao_Paulo", "São Paulo, Brasil (GMT-3)"],
  ["America/New_York", "Nueva York (ET)"],
  ["America/Chicago", "Chicago (CT)"],
  ["America/Los_Angeles", "Los Ángeles (PT)"],
  ["Europe/Madrid", "Madrid, España"],
  ["Europe/London", "Londres"],
  ["UTC", "UTC"],
];

function SettingsTab({ t }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/tenants/settings").then((r) => setS({
    display_name: r.data?.display_name || "", locale: r.data?.locale || "es",
    timezone: r.data?.timezone || "",
    enabled: Array.isArray(r.data?.enabled_modules) ? r.data.enabled_modules : TOGGLE_MODULES.map((m) => m.key),
  })).catch(() => {}); }, []);

  const toggle = (k) => setS((p) => ({ ...p, enabled: p.enabled.includes(k) ? p.enabled.filter((x) => x !== k) : [...p.enabled, k] }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/tenants/settings", { display_name: s.display_name, locale: s.locale, timezone: s.timezone, enabled_modules: s.enabled });
      toast.success(t("os.admin2.saved"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  if (!s) return <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="card-surface p-6 max-w-2xl space-y-5" data-testid="admin2-settings">
      <div>
        <label className="text-sm font-medium block mb-1.5">{t("os.admin2.displayName")}</label>
        <input className="field" value={s.display_name} onChange={(e) => setS({ ...s, display_name: e.target.value })} data-testid="admin2-display-name" />
      </div>
      <div>
        <label className="text-sm font-medium block mb-1.5">{t("os.admin2.locale")}</label>
        <select className="field max-w-[200px]" value={s.locale} onChange={(e) => setS({ ...s, locale: e.target.value })} data-testid="admin2-locale">
          <option value="es">Español</option>
          <option value="en">English</option>
        </select>
      </div>
      <div>
        <label className="text-sm font-medium block mb-1.5">Zona horaria de la empresa</label>
        <select className="field max-w-[340px]" value={s.timezone} onChange={(e) => setS({ ...s, timezone: e.target.value })} data-testid="admin2-timezone">
          {TIMEZONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <p className="text-xs text-navy/40 mt-1">Todos los horarios de la plataforma (Flotilla, alertas, reportes) se mostrarán en esta zona.</p>
      </div>
      <div>
        <label className="text-sm font-medium block mb-1">{t("os.admin2.modules")}</label>
        <p className="text-xs text-navy/40 mb-3">{t("os.admin2.modulesHint")}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {TOGGLE_MODULES.map((m) => (
            <label key={m.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`admin2-mod-${m.key}`}>
              <input type="checkbox" checked={s.enabled.includes(m.key)} onChange={() => toggle(m.key)} className="accent-violet" />
              {m.label}
            </label>
          ))}
        </div>
        <button onClick={() => setS((p) => ({ ...p, enabled: TOGGLE_MODULES.map((m) => m.key) }))}
          className="mt-3 text-xs text-violet hover:underline" data-testid="admin2-enable-all">
          {t("os.admin2.enableAll")}
        </button>
      </div>
      <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="admin2-save-settings">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t("os.admin2.save")}
      </button>
    </div>
  );
}

function PoliciesTab({ t }) {
  const [cat, setCat] = useState(null);
  const [policies, setPolicies] = useState({});
  const [role, setRole] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/authz/catalog"), api.get("/authz/policies")]).then(([c, p]) => {
      setCat(c.data); setPolicies(p.data?.policies || {});
      setRole(c.data?.roles?.[0] || "");
    }).catch(() => {});
  }, []);

  const has = (module, action) => (policies[role]?.[module] || []).includes(action);
  const toggle = (module, action) => setPolicies((prev) => {
    const rp = { ...(prev[role] || {}) };
    const acts = new Set(rp[module] || []);
    acts.has(action) ? acts.delete(action) : acts.add(action);
    rp[module] = [...acts];
    if (rp[module].length === 0) delete rp[module];
    return { ...prev, [role]: rp };
  });

  const save = async () => {
    setSaving(true);
    try { await api.put("/authz/policies", { policies }); toast.success(t("os.admin2.policiesSaved")); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  if (!cat) return <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="admin2-policies">
      <p className="text-xs text-navy/50 max-w-3xl">{t("os.admin2.policiesHint")}</p>
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium">{t("os.admin2.role")}</label>
        <select className="field max-w-[220px]" value={role} onChange={(e) => setRole(e.target.value)} data-testid="admin2-role-select">
          {cat.roles.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button onClick={save} disabled={saving} className="btn btn-primary ml-auto" data-testid="admin2-save-policies">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t("os.admin2.save")}
        </button>
      </div>
      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-navy/10 text-left text-navy/50">
              <th className="p-3 font-medium">Módulo</th>
              {cat.actions.map((a) => <th key={a} className="p-3 font-medium text-center text-xs">{a}</th>)}
            </tr>
          </thead>
          <tbody>
            {cat.modules.map((m) => (
              <tr key={m} className="border-b border-navy/5" data-testid={`admin2-policy-row-${m}`}>
                <td className="p-3 font-medium">{m}</td>
                {cat.actions.map((a) => (
                  <td key={a} className="p-3 text-center">
                    <input type="checkbox" checked={has(m, a)} onChange={() => toggle(m, a)} className="accent-violet"
                      data-testid={`admin2-policy-${m}-${a}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Administracion() {
  const { t } = useI18n();
  const [tab, setTab] = useState("settings");

  return (
    <OSLayout title="Administración">
      <div className="space-y-8" data-testid="administracion-page">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
            <div className="overline text-navy/40 mb-2 flex items-center gap-2"><Shield className="w-3.5 h-3.5" /> {t("os.admin2.overline")}</div>
            <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("os.admin2.title")}</h2>
            <p className="mt-2 text-navy/50 max-w-2xl">{t("os.admin2.subtitle")}</p>
          </motion.div>
          <Link to="/os/admin" className="btn btn-outline text-sm" data-testid="admin2-users-link">
            <ExternalLink className="w-4 h-4" /> {t("os.admin2.usersLink")}
          </Link>
        </div>

        <div className="flex gap-2">
          <button onClick={() => setTab("settings")} data-testid="admin2-tab-settings"
            className={`btn text-sm ${tab === "settings" ? "btn-primary" : "btn-ghost"}`}><SlidersHorizontal className="w-4 h-4" /> {t("os.admin2.tabSettings")}</button>
          <button onClick={() => setTab("policies")} data-testid="admin2-tab-policies"
            className={`btn text-sm ${tab === "policies" ? "btn-primary" : "btn-ghost"}`}><KeyRound className="w-4 h-4" /> {t("os.admin2.tabPolicies")}</button>
        </div>

        {tab === "settings" ? <SettingsTab t={t} /> : <PoliciesTab t={t} />}
      </div>
    </OSLayout>
  );
}
